import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  AnalyticsMetricPointDto,
  AnalyticsRoomSeriesDto,
  AnalyticsUnit,
  PokerSiteId,
} from '@poker-stats/contracts';
import { loadAnalyticsOverview, loadAnalyticsRooms } from '../../containers/AdminHomePage/reducer';
import {
  selectAnalyticsOverview,
  selectAnalyticsOverviewError,
  selectAnalyticsOverviewLoading,
  selectAnalyticsOverviewUnit,
  selectAnalyticsRooms,
  selectAnalyticsRoomsError,
  selectAnalyticsRoomsLoading,
  selectAnalyticsRoomsUnit,
} from '../../containers/AdminHomePage/selectors';
import { AdminLoading, AdminNotice, AdminTable } from '../components/V2AdminUI';
import './analytics.css';

const UNIT_OPTIONS = [
  { label: 'admin_analytics_unit_day', value: 'day' },
  { label: 'admin_analytics_unit_week', value: 'week' },
  { label: 'admin_analytics_unit_month', value: 'month' },
  { label: 'admin_analytics_unit_year', value: 'year' },
] as const satisfies ReadonlyArray<{ label: string; value: AnalyticsUnit }>;

const ROOM_LABELS: Record<PokerSiteId, string> = {
  1: 'PS',
  2: 'CHI',
  3: 'WPN',
  4: '888',
  5: 'CPR',
  6: 'IP',
};

const ROOM_COLORS: Record<PokerSiteId, string> = {
  1: 'var(--acid)',
  2: '#55c983',
  3: '#f0b95b',
  4: '#c4a0ff',
  5: '#ff9aab',
  6: '#54d1c5',
};

const CHART_TOOLTIP_STYLE = {
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: 4,
  color: 'var(--text)',
};

type MetricKey =
  | 'averageDau'
  | 'averageWau'
  | 'averageMau'
  | 'dauMauPercent'
  | 'successfulSearches'
  | 'searchesPerActiveUserDay';

const METRICS = [
  { key: 'averageDau', title: 'admin_analytics_dau', hint: 'admin_analytics_dau_hint' },
  { key: 'averageWau', title: 'admin_analytics_wau', hint: 'admin_analytics_wau_hint' },
  { key: 'averageMau', title: 'admin_analytics_mau', hint: 'admin_analytics_mau_hint' },
  {
    key: 'dauMauPercent',
    title: 'admin_analytics_dau_mau',
    hint: 'admin_analytics_dau_mau_hint',
    percent: true,
  },
  {
    key: 'successfulSearches',
    title: 'admin_analytics_searches',
    hint: 'admin_analytics_searches_hint',
  },
  {
    key: 'searchesPerActiveUserDay',
    title: 'admin_analytics_searches_per_active',
    hint: 'admin_analytics_searches_per_active_hint',
  },
] as const satisfies ReadonlyArray<{
  key: MetricKey;
  title: string;
  hint: string;
  percent?: boolean;
}>;

function UnitFilter({
  value,
  label,
  onChange,
}: {
  value: AnalyticsUnit;
  label: string;
  onChange: (value: AnalyticsUnit) => void;
}) {
  const { t } = useTranslation('translation');
  const name = React.useId();

  return (
    <fieldset className="v2-analytics-units" aria-label={label}>
      {UNIT_OPTIONS.map((option) => (
        <label key={option.value} className="v2-analytics-units__option">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>{t(option.label)}</span>
        </label>
      ))}
    </fieldset>
  );
}

function MetricCard({
  points,
  metric,
}: {
  points: AnalyticsMetricPointDto[];
  metric: (typeof METRICS)[number];
}) {
  const { t, i18n } = useTranslation('translation');
  const titleId = React.useId();
  const currentValue = points[points.length - 1]?.[metric.key] ?? null;
  const previousValue = points[points.length - 2]?.[metric.key] ?? null;
  const delta =
    currentValue == null || previousValue == null || previousValue === 0
      ? null
      : ((currentValue - previousValue) / previousValue) * 100;
  const chartData = points.map((point) => ({
    label: `${point.label}${point.isPartial ? '*' : ''}`,
    value: point[metric.key],
  }));
  const formatter = React.useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language],
  );

  return (
    <article className="v2-analytics-metric" aria-labelledby={titleId}>
      <div className="v2-analytics-metric__header">
        <h3 id={titleId}>{t(metric.title)}</h3>
        <details className="v2-analytics-hint">
          <summary aria-label={t(metric.hint)}>
            <span aria-hidden="true">i</span>
          </summary>
          <p>{t(metric.hint)}</p>
        </details>
      </div>
      <div className="v2-analytics-metric__values">
        <strong className="v2-analytics-metric__value">
          {currentValue == null ? '—' : formatter.format(currentValue)}
          {'percent' in metric && metric.percent && currentValue != null ? '%' : ''}
        </strong>
        <span
          className={`v2-analytics-metric__delta${
            delta == null ? '' : delta >= 0 ? ' is-positive' : ' is-negative'
          }`}
        >
          {delta == null ? '—' : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`}
        </span>
      </div>
      <div className="v2-analytics-metric__chart" role="group" aria-label={t(metric.title)}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            accessibilityLayer
            data={chartData}
            margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
          >
            <ChartTooltip
              formatter={(value: number) => [formatter.format(value), t(metric.title)]}
              contentStyle={CHART_TOOLTIP_STYLE}
            />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--muted)', fontSize: 11 }}
            />
            <Line
              dataKey="value"
              type="monotone"
              stroke="var(--acid)"
              strokeWidth={2}
              dot={{ r: 3, fill: 'var(--acid)' }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}

const V2Analytics: React.FC = () => {
  const { t, i18n } = useTranslation('translation');
  const dispatch = useDispatch();
  const overview = useSelector(selectAnalyticsOverview);
  const rooms = useSelector(selectAnalyticsRooms);
  const overviewUnit = useSelector(selectAnalyticsOverviewUnit);
  const roomsUnit = useSelector(selectAnalyticsRoomsUnit);
  const overviewLoading = useSelector(selectAnalyticsOverviewLoading);
  const roomsLoading = useSelector(selectAnalyticsRoomsLoading);
  const overviewError = useSelector(selectAnalyticsOverviewError);
  const roomsError = useSelector(selectAnalyticsRoomsError);
  const [selectedRooms, setSelectedRooms] = React.useState<PokerSiteId[]>([1, 2, 3, 4, 5, 6]);
  const overviewTitleId = React.useId();
  const roomsTitleId = React.useId();

  React.useEffect(() => {
    dispatch(loadAnalyticsOverview('day'));
    dispatch(loadAnalyticsRooms('day'));
  }, [dispatch]);

  const numberFormatter = React.useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language],
  );

  const roomChartData = React.useMemo(
    () =>
      rooms?.periods.map((period, index) => {
        const point: Record<string, string | number> = {
          label: `${period.label}${period.isPartial ? '*' : ''}`,
        };
        for (const room of rooms.rooms) {
          point[String(room.pokersiteId)] = room.points[index]?.searches ?? 0;
        }
        return point;
      }) ?? [],
    [rooms],
  );

  return (
    <div className="v2-analytics">
      <section
        className="v2-analytics-section"
        aria-labelledby={overviewTitleId}
        aria-busy={overviewLoading}
      >
        <div className="v2-analytics-section__header">
          <h2 id={overviewTitleId}>{t('admin_analytics_activity')}</h2>
          <UnitFilter
            value={overviewUnit}
            label={t('admin_analytics_activity')}
            onChange={(unit) => dispatch(loadAnalyticsOverview(unit))}
          />
        </div>
        {overviewError && (
          <AdminNotice
            type="error"
            action={
              <button
                className="v2-admin-button"
                type="button"
                onClick={() => dispatch(loadAnalyticsOverview(overviewUnit))}
              >
                {t('retry')}
              </button>
            }
          >
            {overviewError}
          </AdminNotice>
        )}
        {overviewLoading && <AdminLoading />}
        {!overview && !overviewLoading && !overviewError && (
          <AdminNotice>{t('admin_analytics_empty')}</AdminNotice>
        )}
        {overview && (
          <>
            {overview.dataAvailableFrom.searches && (
              <AdminNotice>
                {t('admin_analytics_available_from', {
                  date: overview.dataAvailableFrom.searches,
                })}
              </AdminNotice>
            )}
            <div className="v2-analytics-metric-grid">
              {METRICS.map((metric) => (
                <MetricCard key={metric.key} points={overview.points} metric={metric} />
              ))}
            </div>
            <section className="v2-analytics-registration">
              <div>
                <h2>{t('admin_analytics_registration')}</h2>
                <p className="v2-analytics-description">{t('admin_analytics_registration_hint')}</p>
              </div>
              <dl className="v2-analytics-registration__stats">
                <div>
                  <dt>{t('admin_analytics_registrations')}</dt>
                  <dd>{numberFormatter.format(overview.registration.registrations)}</dd>
                </div>
                <div>
                  <dt>{t('admin_analytics_confirmations')}</dt>
                  <dd>{numberFormatter.format(overview.registration.confirmations)}</dd>
                </div>
                <div>
                  <dt>{t('admin_analytics_confirmed_7d')}</dt>
                  <dd>
                    {overview.registration.confirmedWithinSevenDaysPercent == null
                      ? '—'
                      : `${numberFormatter.format(
                          overview.registration.confirmedWithinSevenDaysPercent,
                        )}%`}
                  </dd>
                </div>
                <div>
                  <dt>{t('admin_analytics_pending_7d')}</dt>
                  <dd>
                    {numberFormatter.format(overview.registration.pendingConfirmationOverSevenDays)}
                  </dd>
                </div>
              </dl>
              {overview.registration.confirmationCohortIsForming && (
                <p className="v2-analytics-description">
                  {t('admin_analytics_confirmation_cohort_forming')}
                </p>
              )}
              <div
                className="v2-analytics-large-chart"
                role="group"
                aria-label={t('admin_analytics_registration')}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    accessibilityLayer
                    data={overview.registration.points}
                    margin={{ top: 12, right: 20, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid stroke="var(--line)" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} tick={{ fill: 'var(--muted)' }} />
                    <YAxis allowDecimals={false} tickLine={false} tick={{ fill: 'var(--muted)' }} />
                    <ChartTooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Legend />
                    <Line
                      name={t('admin_analytics_registrations')}
                      dataKey="registrations"
                      type="monotone"
                      stroke="var(--acid)"
                      strokeWidth={2}
                    />
                    <Line
                      name={t('admin_analytics_confirmations')}
                      dataKey="confirmations"
                      type="monotone"
                      stroke="#55c983"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          </>
        )}
      </section>
      <section
        className="v2-analytics-section"
        aria-labelledby={roomsTitleId}
        aria-busy={roomsLoading}
      >
        <div className="v2-analytics-section__header">
          <div>
            <h2 id={roomsTitleId}>{t('admin_analytics_rooms')}</h2>
            <p className="v2-analytics-description">{t('admin_analytics_rooms_hint')}</p>
          </div>
          <UnitFilter
            value={roomsUnit}
            label={t('admin_analytics_rooms')}
            onChange={(unit) => dispatch(loadAnalyticsRooms(unit))}
          />
        </div>
        {roomsError && (
          <AdminNotice
            type="error"
            action={
              <button
                className="v2-admin-button"
                type="button"
                onClick={() => dispatch(loadAnalyticsRooms(roomsUnit))}
              >
                {t('retry')}
              </button>
            }
          >
            {roomsError}
          </AdminNotice>
        )}
        {roomsLoading && <AdminLoading />}
        {!rooms && !roomsLoading && !roomsError && (
          <AdminNotice>{t('admin_analytics_empty')}</AdminNotice>
        )}
        {rooms && (
          <>
            <fieldset className="v2-analytics-room-toggles" aria-label={t('admin_analytics_rooms')}>
              {rooms.rooms.map((room) => (
                <label key={room.pokersiteId} className="v2-analytics-room-toggle">
                  <input
                    className="v2-admin-checkbox"
                    type="checkbox"
                    checked={selectedRooms.includes(room.pokersiteId)}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setSelectedRooms((current) =>
                        checked
                          ? [...current, room.pokersiteId]
                          : current.filter((id) => id !== room.pokersiteId),
                      );
                    }}
                  />
                  <span
                    className="v2-analytics-room-toggle__color"
                    style={{ background: ROOM_COLORS[room.pokersiteId] }}
                    aria-hidden="true"
                  />
                  <span>{ROOM_LABELS[room.pokersiteId]}</span>
                </label>
              ))}
            </fieldset>
            <div
              className="v2-analytics-large-chart"
              role="group"
              aria-label={t('admin_analytics_rooms')}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  accessibilityLayer
                  data={roomChartData}
                  margin={{ top: 12, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid stroke="var(--line)" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} tick={{ fill: 'var(--muted)' }} />
                  <YAxis allowDecimals={false} tickLine={false} tick={{ fill: 'var(--muted)' }} />
                  <ChartTooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Legend />
                  {rooms.rooms
                    .filter((room) => selectedRooms.includes(room.pokersiteId))
                    .map((room) => (
                      <Line
                        key={room.pokersiteId}
                        name={ROOM_LABELS[room.pokersiteId]}
                        dataKey={String(room.pokersiteId)}
                        type="monotone"
                        stroke={ROOM_COLORS[room.pokersiteId]}
                        strokeWidth={2}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <AdminTable<AnalyticsRoomSeriesDto>
              rowKey="pokersiteId"
              rows={rooms.rooms}
              minWidth={560}
              caption={t('admin_analytics_rooms')}
              columns={[
                {
                  key: 'room',
                  title: t('admin_analytics_room'),
                  render: (room) => ROOM_LABELS[room.pokersiteId],
                },
                {
                  key: 'searches',
                  title: t('admin_analytics_searches'),
                  render: (room) => numberFormatter.format(room.totalSearches),
                },
                {
                  key: 'users',
                  title: t('admin_analytics_unique_users'),
                  render: (room) => numberFormatter.format(room.uniqueUsers),
                },
                {
                  key: 'share',
                  title: t('admin_analytics_share'),
                  render: (room) => `${numberFormatter.format(room.sharePercent)}%`,
                },
              ]}
            />
          </>
        )}
        <p className="v2-analytics-description">{t('admin_analytics_partial_note')}</p>
      </section>
    </div>
  );
};

export default V2Analytics;
